import { useState, useEffect } from 'react';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Download, Database, FileJson, FileSpreadsheet, Loader2, 
  CheckCircle2, HardDrive, RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PlatformDataExportPage({ user, onLogout }) {
  const [collections, setCollections] = useState([]);
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [exportFormat, setExportFormat] = useState('json');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadCollections();
  }, []);

  const loadCollections = async () => {
    try {
      const response = await axios.get(`${API}/platform/export/collections`);
      setCollections(response.data.collections);
    } catch (error) {
      toast.error('Failed to load collections');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedCollections.length === collections.length) {
      setSelectedCollections([]);
    } else {
      setSelectedCollections(collections.map(c => c.name));
    }
  };

  const handleToggleCollection = (name) => {
    if (selectedCollections.includes(name)) {
      setSelectedCollections(selectedCollections.filter(c => c !== name));
    } else {
      setSelectedCollections([...selectedCollections, name]);
    }
  };

  const handleExport = async () => {
    if (selectedCollections.length === 0) {
      toast.error('Please select at least one collection to export');
      return;
    }

    setExporting(true);
    try {
      const response = await axios.post(`${API}/platform/export/data`, {
        collections: selectedCollections,
        format: exportFormat
      });

      const { data, format, exported_at, collection_counts } = response.data;

      if (format === 'json') {
        // Download as single JSON file
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `djcrm_export_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        const totalRecords = Object.values(collection_counts).reduce((a, b) => a + b, 0);
        toast.success(`Exported ${totalRecords} records from ${selectedCollections.length} collections`);
      } else {
        // Download as ZIP with multiple CSV files
        // For simplicity, we'll download each CSV separately or combine them
        // Let's create a combined approach - download as multiple files or single combined
        
        const csvEntries = Object.entries(data).filter(([_, csv]) => csv.length > 0);
        
        if (csvEntries.length === 1) {
          // Single CSV file
          const [name, csv] = csvEntries[0];
          const blob = new Blob([csv], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${name}_export_${new Date().toISOString().split('T')[0]}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } else {
          // Multiple CSV files - download each one
          for (const [name, csv] of csvEntries) {
            if (csv) {
              const blob = new Blob([csv], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${name}_export_${new Date().toISOString().split('T')[0]}.csv`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
              // Small delay between downloads
              await new Promise(resolve => setTimeout(resolve, 200));
            }
          }
        }
        
        const totalRecords = Object.values(collection_counts).reduce((a, b) => a + b, 0);
        toast.success(`Exported ${totalRecords} records as CSV files`);
      }
    } catch (error) {
      toast.error('Export failed: ' + (error.response?.data?.detail || error.message));
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const totalDocuments = collections.reduce((sum, c) => sum + c.document_count, 0);
  const selectedDocuments = collections
    .filter(c => selectedCollections.includes(c.name))
    .reduce((sum, c) => sum + c.document_count, 0);

  return (
    <div className="min-h-screen bg-background flex" data-testid="platform-export-page">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="flex-1 p-8 ml-64">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
              <Database className="w-8 h-8 text-neon-blue" />
              Data Export
            </h1>
            <p className="text-muted-foreground mt-2">
              Export your platform data for backup or migration purposes.
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <Card className="glass-card border-glass-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-neon-blue/20">
                    <HardDrive className="w-5 h-5 text-neon-blue" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Collections</p>
                    <p className="text-2xl font-bold">{collections.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="glass-card border-glass-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-500/20">
                    <Database className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Documents</p>
                    <p className="text-2xl font-bold">{totalDocuments.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="glass-card border-glass-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-yellow-500/20">
                    <CheckCircle2 className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Selected for Export</p>
                    <p className="text-2xl font-bold">{selectedDocuments.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Export Options */}
          <Card className="glass-card border-glass-border mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5" />
                Export Format
              </CardTitle>
              <CardDescription>
                Choose the format for your data export
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup 
                value={exportFormat} 
                onValueChange={setExportFormat}
                className="flex gap-6"
              >
                <div className="flex items-center space-x-3 p-4 rounded-lg border border-border hover:border-neon-blue/50 cursor-pointer transition-colors">
                  <RadioGroupItem value="json" id="json" />
                  <Label htmlFor="json" className="flex items-center gap-2 cursor-pointer">
                    <FileJson className="w-5 h-5 text-yellow-500" />
                    <div>
                      <p className="font-medium">JSON</p>
                      <p className="text-xs text-muted-foreground">Best for backups & full data</p>
                    </div>
                  </Label>
                </div>
                
                <div className="flex items-center space-x-3 p-4 rounded-lg border border-border hover:border-neon-blue/50 cursor-pointer transition-colors">
                  <RadioGroupItem value="csv" id="csv" />
                  <Label htmlFor="csv" className="flex items-center gap-2 cursor-pointer">
                    <FileSpreadsheet className="w-5 h-5 text-green-500" />
                    <div>
                      <p className="font-medium">CSV</p>
                      <p className="text-xs text-muted-foreground">Best for spreadsheets & reports</p>
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Collection Selection */}
          <Card className="glass-card border-glass-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="w-5 h-5" />
                    Select Collections
                  </CardTitle>
                  <CardDescription>
                    Choose which data collections to include in the export
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadCollections}
                    disabled={loading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAll}
                  >
                    {selectedCollections.length === collections.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {collections.map((collection) => (
                    <div
                      key={collection.name}
                      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedCollections.includes(collection.name)
                          ? 'border-neon-blue bg-neon-blue/10'
                          : 'border-border hover:border-border/80'
                      }`}
                      onClick={() => handleToggleCollection(collection.name)}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selectedCollections.includes(collection.name)}
                          onCheckedChange={() => handleToggleCollection(collection.name)}
                        />
                        <div>
                          <p className="font-medium text-sm">{collection.description}</p>
                          <p className="text-xs text-muted-foreground">{collection.name}</p>
                        </div>
                      </div>
                      <span className="text-sm font-mono bg-background/50 px-2 py-1 rounded">
                        {collection.document_count.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Export Button */}
          <div className="mt-6 flex justify-end">
            <Button
              size="lg"
              onClick={handleExport}
              disabled={exporting || selectedCollections.length === 0}
              className="bg-neon-blue hover:bg-neon-blue/80"
              data-testid="export-button"
            >
              {exporting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5 mr-2" />
                  Export {selectedCollections.length > 0 ? `${selectedCollections.length} Collection${selectedCollections.length > 1 ? 's' : ''}` : 'Data'}
                </>
              )}
            </Button>
          </div>

          {/* Info Box */}
          <div className="mt-6 p-4 rounded-lg bg-blue-500/10 border border-blue-500/30 text-sm">
            <p className="font-medium text-blue-400 mb-1">Export Information</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              <li>JSON format includes all data fields and is best for complete backups</li>
              <li>CSV format creates separate files for each collection (spreadsheet-friendly)</li>
              <li>Large exports may take a few moments to process</li>
              <li>Sensitive data like passwords are not included in exports</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
